import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PersonSection from './meeting/PersonSection';
import { INSIGHT_CATEGORIES, flattenCategory } from './meeting/InsightQuestion';
import PersonRail from './meeting/PersonRail';
import NowViewing from './meeting/NowViewing';
import WeekSwitcher from './meeting/WeekSwitcher';
import MeetingMarketingReport from './meeting/MeetingMarketingReport';
import TeamOverview from './meeting/TeamOverview';
import { countCarried, revenueOf, capturedRevenueOf, pct, initials, frozenLabel, teamRevenueTotals } from './meeting/metrics';
import { toneFor } from './meeting/personTone';
import './MeetingDetail.css';

const API_BASE = '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Salespeople hidden from the dashboard UI. Their data is left intact — we hide
// the name without deleting history, so anything aggregating across all
// salespeople still counts them.
const HIDDEN_SALESPEOPLE = ['KC'];

export default function MeetingDetail() {
  const { id } = useParams();
  const { user, isSuperadmin } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [meetings, setMeetings] = useState([]);
  const [current, setCurrent] = useState(0);
  const [marketingActive, setMarketingActive] = useState(false);
  const [topbarH, setTopbarH] = useState(64);
  // Which people's cards are unlocked, keyed by salesperson id. Per person, not a
  // single "which card is open" value: two people can edit their own sections at
  // the same time, and one person pressing Edit must never unlock anyone else's.
  const [unlocked, setUnlocked] = useState({});
  const spySections = data?.sections;

  useEffect(() => {
    axios.get(`${API_BASE}/meetings`, { headers: headers() })
      .then(r => setMeetings(Array.isArray(r.data) ? r.data : (r.data.meetings || [])))
      .catch(() => setMeetings([]));   // the switcher is optional chrome; never block the page
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/meetings/${id}`, { headers: headers() });
      // Filter once here so every section, and anything derived from it, agrees on who exists.
      setData({
        ...r.data,
        sections: (r.data.sections || []).filter(s => !HIDDEN_SALESPEOPLE.includes(s.salesperson_name)),
      });
      setError('');
    } catch (e) { setError(e.response?.data?.error || 'Failed to load meeting'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // The "now viewing" strip pins directly under the topbar. The topbar's
  // height isn't a fixed constant in code (font rendering), so measure it
  // rather than hardcode a guess that would drift out of sync. Falls back
  // to a reasonable default (and is simply never used) wherever there is no
  // `.topbar` ancestor, e.g. this page rendered standalone in tests.
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('.topbar');
      if (el) setTopbarH(el.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Scroll-spy: which person section is the reader currently looking at.
  // Mirrors the approved mockup's own algorithm (a section "counts" once its
  // top has scrolled past a fixed offset below the viewport top) but batches
  // the DOM reads into a single rAF per scroll burst, rather than reading
  // layout synchronously on every fired scroll event, so it can't thrash.
  useEffect(() => {
    if (!spySections || !spySections.length) return undefined;
    let ticking = false;
    const compute = () => {
      ticking = false;
      const nodes = document.querySelectorAll('.ml-person');
      if (!nodes.length) return;
      const y = window.scrollY + 150;
      const marketing = document.getElementById('meeting-marketing');
      // Marketing is the last item, so the browser often cannot scroll its top all
      // the way to the normal spy line (there is no content after it). Treat it as
      // active at the bottom of the page whenever the report is visible; this also
      // makes its fixed jump link reliably land on "Marketing · 5 of 5".
      const marketingRect = marketing?.getBoundingClientRect();
      const atPageEnd = Math.ceil(window.scrollY + window.innerHeight)
        >= document.documentElement.scrollHeight - 2;
      const marketingVisible = marketingRect
        && marketingRect.top < window.innerHeight
        && marketingRect.bottom > 0;
      if (marketing && (marketingRect.top <= Math.max(150, window.innerHeight * 0.55)
        || (atPageEnd && marketingVisible))) {
        setMarketingActive(true);
        return;
      }
      setMarketingActive(false);
      let c = 0;
      nodes.forEach((node, i) => { if (node.offsetTop <= y) c = i; });
      setCurrent(prev => (prev === c ? prev : c));
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [spySections]);

  // Who is PERMITTED to change a section. Unchanged — the server enforces the same
  // rule, and this only decides whether the card offers an Edit button at all.
  const canEdit = (sp) => isSuperadmin || (user?.salesperson_id != null && Number(user.salesperson_id) === Number(sp));

  // Edit / Done. Nothing is saved or reverted here: every field already saves as you
  // leave it, so this only turns the card's fields back into plain text.
  const toggleEdit = (sp) => setUnlocked(prev => ({ ...prev, [sp]: !prev[sp] }));

  // The section PUT replaces the whole row, so every save sends the section's
  // full current state and overrides only what changed. Omitting a field here
  // does not leave it alone — it stores it empty.
  const putSection = async (sec, changed) => {
    setError('');
    const payload = {
      rr_text: sec.rr_text || '',
      market_insights_text: sec.market_insights_text || '',
      challenges_text: sec.challenges_text || '',
      rr_items: sec.rr_items || {},
      market_items: sec.market_items || {},
      challenges_items: sec.challenges_items || {},
      checkins: sec.checkins || {},   // preserve the stored scorecard
      ...changed,
    };
    try {
      await axios.put(`${API_BASE}/meetings/${id}/sections/${sec.salesperson_id}`, payload, { headers: headers() });
      load();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
  };

  // ---- Scorecard: the typed half (targets + this week's site visits) ----
  // The prefilled half is derived server-side and never sent back; only the stored
  // scorecard object goes, exactly where it already lived.
  const saveScorecard = (sec, nextScorecard) =>
    putSection(sec, { checkins: { ...(sec.checkins || {}), scorecard: nextScorecard } });

  // ---- Insights: one category's whole question-keyed object ----
  // The old flat TEXT column is regenerated from the items on every save, so the
  // two copies cannot drift while both exist. Temporary: it pairs with the
  // migration's fallback and goes when the TEXT columns are dropped.
  const saveInsightItems = (sec, category, nextItems) => {
    const meta = INSIGHT_CATEGORIES.find(c => c.category === category);
    if (!meta) return;
    return putSection(sec, {
      [meta.itemsField]: nextItems,
      [meta.textField]: flattenCategory(category, nextItems),
    });
  };

  if (error) return <div className="md-error">{error}</div>;
  if (!data) return <div className="md-loading">Loading…</div>;

  const carriedTotal = countCarried(data?.sections);
  // Set by the weekly carry to the id of the same-month meeting the insight
  // answers were copied from; null at a month boundary (they started blank).
  const insightsCarried = Boolean(data?.meeting?.insights_carried_from);

  // One row of identity data per person, shared by the jump rail and the
  // "now viewing" strip — computed here so both agree with the league table
  // and the person cards on the same figures.
  const people = data.sections.map(sec => {
    const delivered = revenueOf(sec);
    const target = sec.checkins?.total?.floor;
    const tracksBookings = sec.checkins?.total?.source === 'booking';
    const captured = tracksBookings ? capturedRevenueOf(sec) : null;
    const capturedTarget = tracksBookings ? sec.checkins?.total?.capturedTarget ?? null : null;
    return {
      id: sec.salesperson_id,
      anchorId: `person-${sec.salesperson_id}`,
      name: sec.salesperson_name,
      initials: initials(sec.salesperson_name),
      tone: toneFor(sec),
      delivered,
      target,
      pct: pct(delivered, target),
      tracksBookings,
      captured,
      capturedTarget,
      capturedPct: pct(captured, capturedTarget),
    };
  });
  const currentIdx = Math.min(current, Math.max(people.length - 1, 0));
  const hasMarketing = Boolean(data.marketing_report);
  const currentPerson = marketingActive && hasMarketing
    ? { name: 'Marketing', initials: 'MK', tone: 'var(--rmc-blue)', kind: 'marketing' }
    : people[currentIdx];
  const currentAgendaIndex = marketingActive && hasMarketing ? people.length : currentIdx;
  const agendaTotal = people.length + (hasMarketing ? 1 : 0);
  // Every section agrees on `frozen` — the read path computes live for the whole
  // meeting unless EVERY section is frozen, so the first section that has one
  // speaks for the meeting.
  const frozen = data.sections.find(sec => sec.frozen)?.frozen || null;
  const teamYtdTotal = teamRevenueTotals(data.sections).ytdDelivered;

  return (
    <div className="meeting-detail">
      <WeekSwitcher meetings={meetings} currentId={id} />
      {(carriedTotal > 0 || insightsCarried) && (
        <div className="ml-banner">
          {carriedTotal > 0 && (
            <><b>{carriedTotal} {carriedTotal === 1 ? 'deal' : 'deals'} carried forward</b> from
            the previous meeting. Won and lost deals stay in the week they closed;
            find them under Closed deals. </>
          )}
          {/* The API says whether insights were carried (meeting.insights_carried_from);
              within a month they carry, and the first meeting of a month starts blank. */}
          {insightsCarried
            ? 'Insight answers were carried over from last week and can be edited.'
            : 'Insight answers start blank for this week.'}
        </div>
      )}

      <NowViewing person={currentPerson} index={currentAgendaIndex} total={agendaTotal} topOffset={topbarH} />

      <header className="md-pagehead">
        <div>
          <div className="md-eyebrow">Sales Meeting</div>
          <h2 className="md-title">{data.meeting.meeting_date}</h2>
          <div className="md-freshness">{frozenLabel(frozen)}</div>
        </div>
        {data.meeting.meeting_time && <span className="md-timepill">{data.meeting.meeting_time}</span>}
      </header>

      <div className="ml-shell">
        <PersonRail people={people} current={currentIdx}
                    showMarketing={hasMarketing} marketingActive={marketingActive} />

        <div className="ml-main">
          <TeamOverview sections={data.sections} meetingDate={data.meeting.meeting_date} />

          {data.sections.map(sec => (
            <PersonSection
              key={sec.salesperson_id}
              sec={sec}
              meetingId={id}
              month={data.meeting.month_key || data.meeting.meeting_date}
              meetingDate={data.meeting.meeting_date}
              teamYtdTotal={teamYtdTotal}
              editable={canEdit(sec.salesperson_id) && !!unlocked[sec.salesperson_id]}
              canEdit={canEdit(sec.salesperson_id)}
              onToggleEdit={() => toggleEdit(sec.salesperson_id)}
              onSaveInsightItems={saveInsightItems}
              onSaveScorecard={saveScorecard}
              onDealsChange={load}
            />
          ))}

          {/* The team report is a fixed final agenda item. Salespeople rotate per
              meeting, but Marketing must never be included in that shuffle. */}
          <MeetingMarketingReport report={data.marketing_report} />
        </div>
      </div>
    </div>
  );
}
